import { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const sizeClasses = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-3xl",
};

const Modal = ({ open, onClose, title, size = "lg", className = "", confirmClose = false, closeRef, scrollResetKey, themedBg = null, themeStyle = null, children }) => {
  const maxWidth = sizeClasses[size] || sizeClasses.lg;
  const [showConfirmBar, setShowConfirmBar] = useState(false);
  const scrollRef = useRef(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setShowConfirmBar(false);
    }
  }, [open]);

  // Reset the scroll container to top whenever the host bumps scrollResetKey —
  // used when a drawer swaps its content in place (e.g. ServiceForm's
  // availability step swap), where the same scroll container is reused and
  // would otherwise inherit the previous scroll offset. Instant, not smooth:
  // the content changed, so there's nothing to animate past.
  //
  // Skip the initial mount — the container starts at the top, so the only
  // meaningful trigger is a later key CHANGE. `scrollTo` is also guarded
  // because jsdom (test env) doesn't implement Element.prototype.scrollTo.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    scrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
  }, [scrollResetKey]);

  const handleCloseAttempt = useCallback(() => {
    if (confirmClose) {
      setShowConfirmBar(true);
      scrollRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    } else {
      onClose();
    }
  }, [confirmClose, onClose]);

  useEffect(() => {
    if (closeRef) {
      closeRef.current = handleCloseAttempt;
    }
  }, [closeRef, handleCloseAttempt]);

  return (
    <Dialog open={open} onClose={handleCloseAttempt} className={`relative z-50 ${className}`}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm transition-opacity duration-300 ease-editorial data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-stretch justify-center sm:items-center sm:p-4">
          <DialogPanel
            transition
            style={themedBg ? themeStyle : undefined}
            className={`relative w-full transform ${themedBg ? 'bg-themed-card' : 'bg-cream-50'} text-left shadow-lift transition-all duration-300 ease-editorial
              data-[closed]:translate-y-4 data-[closed]:opacity-0
              sm:my-4 sm:rounded-card ${maxWidth}
              data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`}
          >
            <div className="flex h-full max-h-screen flex-col sm:max-h-[90vh]">
              <div
                className={`sticky top-0 z-10 flex items-center justify-between border-b ${themedBg ? 'bg-themed-card border-themed' : 'border-ink-100 bg-cream-50/95'} backdrop-blur px-6 pb-4 sm:rounded-t-card sm:pt-4`}
                style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 1rem))' }}
              >
                <DialogTitle className={`font-display text-xl ${themedBg ? 'text-themed-primary' : 'text-ink-900'}`}>
                  {title}
                </DialogTitle>
                <button
                  type="button"
                  onClick={handleCloseAttempt}
                  className={`rounded-full p-1 ${themedBg ? 'text-themed-secondary hover:text-themed-primary hover-themed-tint' : 'text-ink-400 hover:text-ink-700 hover:bg-cream-200'} focus:outline-none focus:ring-2 focus:ring-accent-500/30`}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
                {showConfirmBar && (
                  <div className="mb-4 -mt-2 rounded-card border-y border-r border-signal-amber-100 border-l-4 border-l-signal-amber-500 bg-signal-amber-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-5 w-5 text-signal-amber-500" aria-hidden="true" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h3 className="text-sm font-medium text-signal-amber-600">
                          Unsaved changes
                        </h3>
                        <p className="mt-1 text-sm text-ink-600">
                          You have unsaved changes that will be lost.
                        </p>
                        <div className="mt-3 flex gap-3">
                          <button
                            type="button"
                            onClick={() => setShowConfirmBar(false)}
                            className="rounded-pill bg-white border border-signal-amber-100 px-3 py-1.5 text-sm font-medium text-signal-amber-600 hover:bg-signal-amber-50"
                          >
                            Stay on Page
                          </button>
                          <button
                            type="button"
                            onClick={onClose}
                            className="rounded-pill bg-signal-amber-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-signal-amber-600"
                          >
                            Discard
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {children}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default Modal;
